import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {addGenericErrorToast} from '@albert-io/atomic';
import getFilters from '../getFilters';
import {SortOrder, StudentAssignmentSortField} from 'gql/types';
import type {StudentAssignment, StudentAssignmentsQueryVariables} from '../StudentAssignment.types';
import {useStudentAssignmentsQuery} from '../queries/StudentAssignments.queries.generated';
import type {Value as DropdownValue} from '../FilterDropdown';

const PAGINATION_LIMIT = 25;
const TO_DO_LIMIT = 15;

export default function useAssignments(
  classroomId: string | undefined,
  dropdownValue: DropdownValue,
  searchQuery?: string
) {
  const [hasNextPageAfterLoadingMore, setHasNextPageAfterLoadingMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [moreAssignments, setMoreAssignments] = useState<StudentAssignment[]>([]);

  const isLoadingMoreRef = useRef(false);

  const resetMoreAssignments = useCallback(() => {
    setMoreAssignments([]);
    setHasNextPageAfterLoadingMore(true);
    isLoadingMoreRef.current = false;
    setIsLoadingMore(false);
  }, []);

  const limit = dropdownValue === 'toDo' ? TO_DO_LIMIT : PAGINATION_LIMIT;

  const variables = useMemo<StudentAssignmentsQueryVariables>(
    () => ({
      filters: getFilters(classroomId, dropdownValue, searchQuery),
      limit,
      sort: [
        {
          field: StudentAssignmentSortField.DueDateProximity,
          order: SortOrder.Asc
        },
        {
          field: StudentAssignmentSortField.Name,
          order: SortOrder.Asc
        }
      ]
    }),
    [classroomId, dropdownValue, limit, searchQuery]
  );

  // Reset paginated results when filters change (dropdown, search, classroom)
  const prevVariablesKey = useRef<string>(undefined);
  const variablesKey = useMemo(() => JSON.stringify(variables), [variables]);
  useEffect(() => {
    if (prevVariablesKey.current !== undefined && prevVariablesKey.current !== variablesKey) {
      resetMoreAssignments();
    }
    prevVariablesKey.current = variablesKey;
  }, [variablesKey, resetMoreAssignments]);

  const {data, error, fetchMore, loading, refetch} = useStudentAssignmentsQuery({variables});
  const initialAssignments = data?.currentUser?.studentAssignments.results ?? [];
  const initialHasNextPage = data?.currentUser?.studentAssignments.hasNextPage ?? false;

  const allAssignments = [...initialAssignments, ...moreAssignments];
  const hasNextPage = initialHasNextPage && hasNextPageAfterLoadingMore;

  const offsetRef = useRef(allAssignments.length);
  useEffect(() => {
    offsetRef.current = allAssignments.length;
  });

  const exposedFetchMore = useCallback(() => {
    if (!loading && !isLoadingMoreRef.current && hasNextPage) {
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);

      fetchMore({
        variables: {
          ...variables,
          offset: offsetRef.current
        }
      })
        .then((fetchedData) => {
          const fetchedAssignments = fetchedData.data.currentUser?.studentAssignments.results ?? [];
          const fetchedHasNextPage =
            fetchedData.data.currentUser?.studentAssignments.hasNextPage ?? false;

          setMoreAssignments((current) => [...current, ...fetchedAssignments]);
          setHasNextPageAfterLoadingMore(fetchedHasNextPage);
        })
        .catch(() => {
          addGenericErrorToast();
        })
        .finally(() => {
          isLoadingMoreRef.current = false;
          setIsLoadingMore(false);
        });
    }
  }, [fetchMore, hasNextPage, loading, variables]);

  return {
    assignments: allAssignments,
    fetchMore: exposedFetchMore,
    isError: error !== undefined,
    isLoadingMore,
    loading,
    refetch,
    resetMoreAssignments
  };
}
